import { calculateBoundsFromEncodedBoundary as calculateBounds } from '@/src/lib/map/boundaryBoundsCalculator';

/**
 * Pure boundary-to-viewport math. Extracted from AreaBoundary.tsx so it's
 * safe to import from server components (AreaBoundary pulls client hooks).
 */
export type MapPositionFromBoundary = {
  center: { lat: number; lng: number };
  zoom: number;
  bounds: {
    northLat: number;
    southLat: number;
    eastLng: number;
    westLng: number;
  };
};

export type BoundaryBounds = {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
};

export function calculateCenterAndZoomFromBounds(
  bounds: BoundaryBounds,
  mapWidth = 800,
  mapHeight = 600,
  padding = 60,
): MapPositionFromBoundary | null {
  if (
    !Number.isFinite(bounds.minLat) ||
    !Number.isFinite(bounds.maxLat) ||
    !Number.isFinite(bounds.minLng) ||
    !Number.isFinite(bounds.maxLng)
  ) {
    return null;
  }

  const center = {
    lat: (bounds.maxLat + bounds.minLat) / 2,
    lng: (bounds.maxLng + bounds.minLng) / 2,
  };

  const WORLD_DIM = { height: 256, width: 256 };
  const ZOOM_MAX = 21;

  function latRad(lat: number) {
    const sin = Math.sin((lat * Math.PI) / 180);
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
  }

  function zoom(mapPx: number, worldPx: number, fraction: number) {
    if (fraction <= 0) return ZOOM_MAX;
    return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
  }

  const latFraction = (latRad(bounds.maxLat) - latRad(bounds.minLat)) / Math.PI;
  const lngDiff = bounds.maxLng - bounds.minLng;
  const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

  const latZoom = zoom(mapHeight - padding * 2, WORLD_DIM.height, latFraction);
  const lngZoom = zoom(mapWidth - padding * 2, WORLD_DIM.width, lngFraction);

  return {
    center,
    zoom: Math.min(latZoom, lngZoom, ZOOM_MAX),
    bounds: {
      northLat: bounds.maxLat,
      southLat: bounds.minLat,
      eastLng: bounds.maxLng,
      westLng: bounds.minLng,
    },
  };
}

export function calculateCenterAndZoomFromBoundary(
  encodedBoundary: string | Array<Array<string>>,
  mapWidth = 800,
  mapHeight = 600,
  padding = 60,
): MapPositionFromBoundary | null {
  const bounds = calculateBounds(encodedBoundary);
  if (!bounds) return null;

  return calculateCenterAndZoomFromBounds(bounds, mapWidth, mapHeight, padding);
}
